// Постоянный баннер о скором окончании подписки в веб-админке.
// Показывается владельцу (OWNER), когда до конца подписки ≤ 3 дней (оранжевый)
// или подписка приостановлена — статус PAST_DUE (красный, не сворачивается).
//
// Источник данных — тот же, что у страницы «Подписка» (`getMe` → /api/public/me,
// ключ react-query ['billing/me']), поэтому цифра «осталось N дней» гарантированно
// совпадает со страницей и бэкендом (computeDaysLeft, этап 02).
//
// Почему только OWNER: /api/public/me на бэкенде под `requireOwner`, а биллинг в
// вебе доступен лишь владельцу (страница Subscription тоже enabled: isOwner).
// Для остальных ролей баннера нет — у них нет доступа к статусу подписки.
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, X } from 'lucide-react';

import { useAuthStore } from '@/store/auth.store';
import { getMe } from '@/services/billing';

const WHATSAPP_URL = 'https://wa.me/79894734322';
const HIDE_KEY = 'subBannerHidden';

export function SubscriptionBanner() {
  const isOwner = useAuthStore((s) => s.user?.role) === 'OWNER';
  const [hidden, setHidden] = useState(() => sessionStorage.getItem(HIDE_KEY) === '1');

  const { data: me } = useQuery({
    queryKey: ['billing/me'],
    queryFn: getMe,
    enabled: isOwner,
    staleTime: 60_000,
  });

  const sub = me?.subscription;
  if (!sub) return null;

  const blocked = sub.accessState === 'blocked';
  const grace = sub.accessState === 'grace';
  const critical = blocked || grace || sub.status === 'PAST_DUE';
  const expiring =
    (sub.status === 'ACTIVE' || sub.status === 'TRIAL') &&
    sub.daysLeft != null &&
    sub.daysLeft <= 3;

  if (!critical && !expiring) return null;
  // Оранжевый баннер можно свернуть на сессию; критический — нет.
  if (hidden && !critical) return null;

  // Пока идёт грейс, честно говорим, сколько ещё работает касса: владелец
  // должен понимать, что заказы закроются не «когда-нибудь», а через N дней.
  const msg = blocked
    ? 'Подписка не оплачена: касса не открывает новые заказы и смены. Продлите, чтобы вернуть работу.'
    : grace
      ? `Срок подписки истёк. Касса работает ещё ${daysWord(sub.graceDaysLeft)} — потом новые заказы закроются.`
      : critical
        ? 'Подписка приостановлена. Продлите, чтобы вернуть доступ.'
        : `Подписка заканчивается ${daysPhrase(sub.daysLeft ?? 0)}. Продлите, чтобы не потерять доступ.`;

  return (
    <div className={`${critical ? 'bg-rose-600' : 'bg-amber-500'} text-white sticky top-0 z-20`}>
      <div className="px-4 py-2.5 sm:px-6 flex flex-wrap items-center justify-center gap-x-4 gap-y-2 text-sm">
        <span className="inline-flex items-center gap-2 font-medium text-center">
          <AlertTriangle size={16} className="shrink-0" /> {msg}
        </span>
        <div className="flex items-center gap-3">
          <Link
            to="/subscription"
            className="bg-white/95 text-slate-900 font-semibold px-3.5 py-1.5 rounded-md hover:bg-white transition-colors whitespace-nowrap"
          >
            Продлить
          </Link>
          <a
            href={WHATSAPP_URL}
            target="_blank"
            rel="noreferrer"
            className="underline underline-offset-2 hover:opacity-90 whitespace-nowrap"
          >
            Написать в WhatsApp
          </a>
          {!critical && (
            <button
              type="button"
              aria-label="Скрыть уведомление"
              onClick={() => {
                setHidden(true);
                sessionStorage.setItem(HIDE_KEY, '1');
              }}
              className="p-1 rounded hover:bg-white/15 transition-colors"
            >
              <X size={16} />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// «сегодня» / «завтра» / «через N дней» с корректным русским склонением.
function daysPhrase(days: number): string {
  if (days <= 0) return 'сегодня';
  if (days === 1) return 'завтра';
  return `через ${daysWord(days)}`;
}

// «1 день» / «2 дня» / «5 дней».
function daysWord(days: number): string {
  const mod10 = days % 10;
  const mod100 = days % 100;
  let unit = 'дней';
  if (mod10 === 1 && mod100 !== 11) unit = 'день';
  else if (mod10 >= 2 && mod10 <= 4 && (mod100 < 10 || mod100 >= 20)) unit = 'дня';
  return `${days} ${unit}`;
}
